import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, LogOut, RefreshCw, Phone, Mail, Pencil, Trash2, Plus, X } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { Logo } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "zirsen-admin-token";
const TIME_SLOTS = ["09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00"];

const S = {
  title: { cs: "Přehled schůzek", en: "Bookings Dashboard" },
  sub: { cs: "Příchozí rezervace včetně výsledků Zirsen Analytic Tool.", en: "Incoming bookings incl. Zirsen Analytic Tool results." },
  pass: { cs: "Přístupový kód", en: "Access code" },
  login: { cs: "Přihlásit", en: "Sign in" },
  wrong: { cs: "Nesprávný kód", en: "Wrong passcode" },
  empty: { cs: "Zatím žádné rezervace", en: "No bookings yet" },
  logout: { cs: "Odhlásit", en: "Log out" },
  client: { cs: "Klient", en: "Client" },
  when: { cs: "Termín", en: "Slot" },
  advisor: { cs: "Poradce", en: "Advisor" },
  mode: { cs: "Forma", en: "Format" },
  matrix: { cs: "Analýza", en: "Analysis" },
  matrixIdx: { cs: "Index", en: "Index" },
  reminder: { cs: "Připomínka", en: "Reminder" },
  sent: { cs: "odeslána", en: "sent" },
  pending: { cs: "čeká", en: "pending" },
  actions: { cs: "Akce", en: "Actions" },
  addBtn: { cs: "Přidat schůzku", en: "Add booking" },
  call: { cs: "Zavolat klientovi", en: "Call client" },
  editTitle: { cs: "Upravit schůzku", en: "Edit booking" },
  addTitle: { cs: "Nová schůzka", en: "New booking" },
  save: { cs: "Uložit", en: "Save" },
  cancel: { cs: "Zrušit", en: "Cancel" },
  delConfirm: { cs: "Opravdu zrušit tuto schůzku?", en: "Really cancel this booking?" },
  name: { cs: "Jméno", en: "Name" },
  email: { cs: "E-mail", en: "Email" },
  phone: { cs: "Telefon", en: "Phone" },
  topic: { cs: "Poznámka / téma", en: "Note / topic" },
  statsTitle: { cs: "Návštěvnost a analytika", en: "Traffic & Analytics" },
  statVisits: { cs: "Návštěvy (30 dní)", en: "Visits (30 days)" },
  statToday: { cs: "dnes", en: "today" },
  statSessions: { cs: "Unikátní návštěvy", en: "Unique visits" },
  statStarts: { cs: "Zahájené analýzy", en: "Analyses started" },
  statCompletes: { cs: "Dokončené analýzy", en: "Analyses completed" },
  statCompletion: { cs: "míra dokončení", en: "completion rate" },
  statBookings: { cs: "Rezervace", en: "Bookings" },
  statConversion: { cs: "konverze z analýzy", en: "analysis conversion" },
  chartTitle: { cs: "Posledních 14 dní", en: "Last 14 days" },
  legendPv: { cs: "Návštěvy", en: "Visits" },
  legendStart: { cs: "Zahájení analýzy", en: "Analysis starts" },
  legendDone: { cs: "Dokončení analýzy", en: "Analysis completes" },
  ageTitle: { cs: "Věk dokončených analýz", en: "Age of completed analyses" },
  statLeads: { cs: "Leady z analýzy", en: "Analysis leads" },
  leadsTitle: { cs: "Leady z Finanční analýzy", en: "Analysis Leads" },
  leadsSub: { cs: "Kontakty, které si nechaly poslat výsledek e-mailem.", en: "Contacts who requested their result by email." },
  leadsEmpty: { cs: "Zatím žádné leady z analýzy.", en: "No analysis leads yet." },
  leadScore: { cs: "Index", en: "Index" },
  leadAnswers: { cs: "odpovědí", en: "answers" },
  delLeadConfirm: { cs: "Opravdu smazat tento lead?", en: "Really delete this lead?" },
};

const MODES = { office: { cs: "Kancelář", en: "Office" }, visit: { cs: "U klienta", en: "Client site" }, online: { cs: "Online", en: "Online" } };
const ADVISORS = { raffaele: "Raffaele Zirone", ondrej: "Ondřej Šenfeld", any: "Kdokoliv" };
const MATRIX_KEYS = ["pojisteni", "investice", "sporeni", "hypoteky", "uvery"];
const PILLAR_SHORT = {
  cs: { pojisteni: "Pojiš.", investice: "Invest.", sporeni: "Spoř.", hypoteky: "Hypo.", uvery: "Úvěry" },
  en: { pojisteni: "Insur.", investice: "Invest.", sporeni: "Savings", hypoteky: "Mortg.", uvery: "Loans" },
};
const EMPTY = { name: "", email: "", phone: "", advisor: "any", date: "", time: "09:00", mode: "office", topic: "" };

const inputCls =
  "w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-600/15";

const AGE_BANDS = { cs: ["do 29 let", "30–44 let", "45–59 let", "60+ let"], en: ["under 29", "30–44", "45–59", "60+"] };

const StatCard = ({ label, value, sub, testid }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={testid}>
    <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
    <p className="font-heading mt-2 text-3xl font-extrabold text-[#0A192F]">{value}</p>
    {sub && <p className="mt-1 text-xs font-semibold text-teal-700">{sub}</p>}
  </div>
);

export default function Admin() {
  const { lang } = useLanguage();
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [passcode, setPasscode] = useState("");
  const [bookings, setBookings] = useState(null);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState(null);
  const [error, setError] = useState(false);
  const [modal, setModal] = useState(null);

  const load = async (tk) => {
    try {
      const r = await axios.get(`${API}/bookings`, { headers: { Authorization: `Bearer ${tk}` } });
      setBookings(r.data.sort((a, b) => (a.date + a.time < b.date + b.time ? 1 : -1)));
      axios
        .get(`${API}/analytics/stats`, { headers: { Authorization: `Bearer ${tk}` } })
        .then((s) => setStats(s.data))
        .catch(() => {});
      axios
        .get(`${API}/leads`, { headers: { Authorization: `Bearer ${tk}` } })
        .then((l) => setLeads(l.data))
        .catch(() => setLeads([]));
    } catch {
      sessionStorage.removeItem(TOKEN_KEY);
      setToken("");
    }
  };

  useEffect(() => {
    if (token) load(token);
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setError(false);
    try {
      const r = await axios.post(`${API}/admin/login`, { passcode });
      sessionStorage.setItem(TOKEN_KEY, r.data.token);
      setToken(r.data.token);
    } catch {
      setError(true);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setBookings(null);
  };

  const saveModal = async () => {
    const payload = { ...modal.data, lang: "cs" };
    try {
      if (modal.mode === "edit") {
        await axios.put(`${API}/bookings/${modal.data.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API}/bookings`, payload, { headers: { Authorization: `Bearer ${token}` } });
      }
      setModal(null);
      load(token);
    } catch {
      setError(true);
    }
  };

  const removeBooking = async (id) => {
    if (!window.confirm(S.delConfirm[lang])) return;
    await axios.delete(`${API}/bookings/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    load(token);
  };

  const removeLead = async (id) => {
    if (!window.confirm(S.delLeadConfirm[lang])) return;
    await axios.delete(`${API}/leads/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    load(token);
  };

  const matrixIndex = (m) => {
    const vals = MATRIX_KEYS.map((k) => Number(m?.[k] ?? 0));
    return Math.min(97, Math.max(5, Math.round((1 - vals.reduce((a, b) => a + b, 0) / vals.length / 4) * 100)));
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#050B14] px-4" data-testid="admin-login">
        <motion.form
          onSubmit={login}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          className="mg-glass w-full max-w-sm rounded-2xl p-8 text-center"
        >
          <ShieldCheck size={40} className="mx-auto text-teal-300" />
          <h1 className="font-heading mg-glow-text mt-4 text-2xl font-extrabold text-white">Zirsen Admin</h1>
          <input
            type="password"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            placeholder={S.pass[lang]}
            data-testid="admin-passcode-input"
            className="mt-6 w-full rounded-lg border border-teal-400/25 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-teal-300 focus:outline-none"
          />
          {error && <p className="mt-2 font-mono text-xs text-red-300" data-testid="admin-login-error">{S.wrong[lang]}</p>}
          <button
            type="submit"
            data-testid="admin-login-button"
            className="mt-4 w-full rounded-full bg-teal-500 py-3 text-sm font-bold uppercase tracking-widest text-[#052E2B] transition-colors hover:bg-teal-300"
          >
            {S.login[lang]}
          </button>
        </motion.form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <Logo className="h-10" />
          <div className="flex items-center gap-2">
            <button
              onClick={() => setModal({ mode: "add", data: { ...EMPTY } })}
              data-testid="admin-add-button"
              className="flex items-center gap-1.5 rounded-full bg-[#0A192F] px-4 py-2 text-xs font-semibold text-white hover:bg-teal-700"
            >
              <Plus size={13} /> {S.addBtn[lang]}
            </button>
            <button
              onClick={() => load(token)}
              data-testid="admin-refresh-button"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 hover:border-teal-500 hover:text-teal-700"
              aria-label="Refresh"
            >
              <RefreshCw size={15} />
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 hover:border-red-300 hover:text-red-600"
            >
              <LogOut size={13} /> {S.logout[lang]}
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <h1 className="font-heading text-3xl font-extrabold text-slate-900">{S.title[lang]}</h1>
        <p className="mt-2 text-sm text-slate-500">{S.sub[lang]}</p>

        {stats && (
          <div className="mt-8" data-testid="admin-stats">
            <h2 className="font-heading text-lg font-bold text-slate-900">{S.statsTitle[lang]}</h2>
            <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-6">
              <StatCard label={S.statVisits[lang]} value={stats.pageviews.d30} sub={`${S.statToday[lang]}: ${stats.pageviews.today}`} testid="stat-card-visits" />
              <StatCard label={S.statSessions[lang]} value={stats.sessions} testid="stat-card-sessions" />
              <StatCard label={S.statStarts[lang]} value={stats.analysisStarts} testid="stat-card-starts" />
              <StatCard label={S.statCompletes[lang]} value={stats.analysisCompletes} sub={`${S.statCompletion[lang]}: ${stats.completionRate} %`} testid="stat-card-completes" />
              <StatCard label={S.statBookings[lang]} value={stats.bookings} sub={`${S.statConversion[lang]}: ${stats.bookingConversion} %`} testid="stat-card-bookings" />
              <StatCard label={S.statLeads[lang]} value={stats.leads ?? 0} testid="stat-card-leads" />
            </div>

            <div className="mt-4 grid gap-3 lg:grid-cols-[1.4fr_1fr]">
              <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="stats-chart">
                <div className="flex items-center justify-between">
                  <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-slate-400">{S.chartTitle[lang]}</p>
                  <div className="flex items-center gap-3 text-[10px] font-semibold text-slate-500">
                    <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-teal-500" />{S.legendPv[lang]}</span>
                    <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-amber-400" />{S.legendStart[lang]}</span>
                    <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-[#0A192F]" />{S.legendDone[lang]}</span>
                  </div>
                </div>
                <div className="mt-4 flex h-36 items-end gap-1.5">
                  {stats.daily.map((d) => {
                    const max = Math.max(...stats.daily.map((x) => Math.max(x.pageviews, x.starts, x.completes)), 1);
                    return (
                      <div key={d.date} className="flex h-full flex-1 items-end justify-center gap-0.5" title={`${d.date}: ${d.pageviews} / ${d.starts} / ${d.completes}`}>
                        <div className="w-1.5 rounded-t bg-teal-500" style={{ height: `${Math.max((d.pageviews / max) * 100, 2)}%` }} />
                        <div className="w-1.5 rounded-t bg-amber-400" style={{ height: `${Math.max((d.starts / max) * 100, 2)}%` }} />
                        <div className="w-1.5 rounded-t bg-[#0A192F]" style={{ height: `${Math.max((d.completes / max) * 100, 2)}%` }} />
                      </div>
                    );
                  })}
                </div>
                <div className="mt-1.5 flex justify-between font-mono text-[9px] text-slate-400">
                  <span>{stats.daily[0]?.date.slice(5)}</span>
                  <span>{stats.daily[stats.daily.length - 1]?.date.slice(5)}</span>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="stats-ages">
                <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-slate-400">{S.ageTitle[lang]}</p>
                <div className="mt-4 space-y-3">
                  {AGE_BANDS[lang].map((label, i) => {
                    const n = stats.ages[String(i)] || 0;
                    const maxA = Math.max(...Object.values(stats.ages).map(Number), 1);
                    return (
                      <div key={i}>
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-semibold text-slate-600">{label}</span>
                          <span className="font-mono font-bold text-[#0A192F]">{n}</span>
                        </div>
                        <div className="mt-1 h-2 overflow-hidden rounded-full bg-slate-100">
                          <div className="h-full rounded-full bg-teal-500" style={{ width: `${(n / maxA) * 100}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {leads && (
          <div className="mt-10" data-testid="leads-section">
            <h2 className="font-heading text-lg font-bold text-slate-900">{S.leadsTitle[lang]}</h2>
            <p className="mt-1 text-xs text-slate-400">{S.leadsSub[lang]}</p>
            {leads.length === 0 ? (
              <p className="mt-4 text-sm text-slate-400" data-testid="leads-empty">{S.leadsEmpty[lang]}</p>
            ) : (
              <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {leads.map((l) => (
                  <div key={l.id} data-testid={`lead-card-${l.id}`} className="rounded-2xl border border-slate-200 bg-white p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-slate-900">{l.name || "—"}</p>
                        <p className="truncate text-xs text-slate-500">{l.email}</p>
                        <p className="text-xs text-slate-500">{l.phone || "—"}</p>
                      </div>
                      <span className="shrink-0 rounded-full bg-[#0A192F] px-2.5 py-1 font-mono text-[10px] font-bold text-teal-300">
                        {S.leadScore[lang]} {l.index}/100
                      </span>
                    </div>
                    <p className="mt-2 font-mono text-[10px] uppercase tracking-wider text-slate-400">
                      {String(l.created_at).slice(0, 16).replace("T", " · ")}
                    </p>
                    {l.rows?.length > 0 && (
                      <details className="mt-3" data-testid={`lead-answers-${l.id}`}>
                        <summary className="cursor-pointer text-xs font-semibold text-teal-700">
                          {l.rows.length} {S.leadAnswers[lang]}
                        </summary>
                        <div className="mt-2 max-h-40 space-y-1 overflow-y-auto rounded-lg bg-slate-50 p-3">
                          {l.rows.map((r, j) => (
                            <p key={j} className="text-[11px] text-slate-600">
                              <span className="font-semibold">{r.label}:</span> {r.value}
                            </p>
                          ))}
                        </div>
                      </details>
                    )}
                    <div className="mt-4 flex items-center gap-1.5">
                      <a
                        href={`tel:${l.phone}`}
                        data-testid={`lead-call-${l.id}`}
                        title={S.call[lang]}
                        className="flex h-8 w-8 items-center justify-center rounded-full border border-teal-200 bg-teal-50 text-teal-700 transition-colors hover:bg-teal-600 hover:text-white"
                      >
                        <Phone size={13} />
                      </a>
                      <a
                        href={`mailto:${l.email}`}
                        data-testid={`lead-mail-${l.id}`}
                        title={l.email}
                        className="flex h-8 items-center gap-1.5 rounded-full border border-slate-200 px-3 text-xs font-semibold text-slate-600 transition-colors hover:border-teal-500 hover:text-teal-700"
                      >
                        <Mail size={12} /> E-mail
                      </a>
                      <button
                        onClick={() => removeLead(l.id)}
                        data-testid={`lead-delete-${l.id}`}
                        title={S.delLeadConfirm[lang]}
                        className="ml-auto flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-400 transition-colors hover:border-red-400 hover:text-red-600"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {!bookings ? (
          <p className="mt-10 text-sm text-slate-400">…</p>
        ) : bookings.length === 0 ? (
          <p className="mt-10 text-sm text-slate-500" data-testid="admin-empty">{S.empty[lang]}</p>
        ) : (
          <div className="mt-8 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
            <table className="w-full min-w-[1020px] text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-400">
                  <th className="px-5 py-3.5 font-semibold">{S.when[lang]}</th>
                  <th className="px-5 py-3.5 font-semibold">{S.client[lang]}</th>
                  <th className="px-5 py-3.5 font-semibold">{S.advisor[lang]}</th>
                  <th className="px-5 py-3.5 font-semibold">{S.mode[lang]}</th>
                  <th className="px-5 py-3.5 font-semibold">{S.matrix[lang]}</th>
                  <th className="px-5 py-3.5 font-semibold">{S.reminder[lang]}</th>
                  <th className="px-5 py-3.5 font-semibold">{S.actions[lang]}</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`booking-row-${b.id}`}>
                    <td className="px-5 py-4 font-mono font-semibold text-slate-900">{b.date} · {b.time}</td>
                    <td className="px-5 py-4">
                      <p className="font-semibold text-slate-900">{b.name}</p>
                      <p className="text-xs text-slate-500">{b.email}</p>
                      <p className="text-xs text-slate-500">{b.phone}</p>
                      {b.topic && b.topic !== "Zirsen Analytic Tool" && b.topic !== "Neural-Fin Matrix" && (
                        <p className="mt-1 max-w-[220px] truncate text-xs italic text-slate-400" title={b.topic}>{b.topic}</p>
                      )}
                    </td>
                    <td className="px-5 py-4 text-slate-600">{ADVISORS[b.advisor] || b.advisor}</td>
                    <td className="px-5 py-4 text-slate-600">{(MODES[b.mode] || MODES.office)[lang]}</td>
                    <td className="px-5 py-4">
                      {b.matrix ? (
                        <div>
                          <div className="flex flex-wrap gap-1.5">
                            {MATRIX_KEYS.map((k) => (
                              <span key={k} className="rounded-md bg-[#0A192F] px-2 py-1 font-mono text-[10px] font-bold text-teal-300" data-testid={`matrix-chip-${k}`}>
                                {PILLAR_SHORT[lang][k]} {b.matrix[k] ?? "–"}/4
                              </span>
                            ))}
                          </div>
                          <p className="mt-1.5 font-mono text-[10px] text-slate-400">
                            {S.matrixIdx[lang]}: <span className="font-bold text-teal-700">{matrixIndex(b.matrix)}/100</span>
                          </p>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${b.reminder_sent ? "bg-teal-50 text-teal-700" : "bg-slate-100 text-slate-400"}`}>
                        {b.reminder_sent ? S.sent[lang] : S.pending[lang]}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1.5">
                        <a
                          href={`tel:${b.phone}`}
                          data-testid={`call-button-${b.id}`}
                          title={S.call[lang]}
                          className="flex h-8 w-8 items-center justify-center rounded-full border border-teal-200 bg-teal-50 text-teal-700 transition-colors hover:bg-teal-600 hover:text-white"
                        >
                          <Phone size={13} />
                        </a>
                        <button
                          onClick={() => setModal({ mode: "edit", data: { ...b, topic: b.topic || "" } })}
                          data-testid={`edit-button-${b.id}`}
                          title={S.editTitle[lang]}
                          className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition-colors hover:border-teal-500 hover:text-teal-700"
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          onClick={() => removeBooking(b.id)}
                          data-testid={`delete-button-${b.id}`}
                          title={S.cancel[lang]}
                          className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition-colors hover:border-red-400 hover:text-red-600"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      <AnimatePresence>
        {modal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-[#0A192F]/60 p-4 backdrop-blur-sm"
            onClick={() => setModal(null)}
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.97 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 sm:p-8"
              data-testid="admin-booking-modal"
            >
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-xl font-bold text-slate-900">
                  {modal.mode === "edit" ? S.editTitle[lang] : S.addTitle[lang]}
                </h2>
                <button onClick={() => setModal(null)} className="text-slate-400 hover:text-slate-700" aria-label="Close">
                  <X size={18} />
                </button>
              </div>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                <input value={modal.data.name} onChange={(e) => setModal({ ...modal, data: { ...modal.data, name: e.target.value } })} placeholder={S.name[lang]} className={inputCls} data-testid="modal-name-input" />
                <input value={modal.data.phone} onChange={(e) => setModal({ ...modal, data: { ...modal.data, phone: e.target.value } })} placeholder={S.phone[lang]} className={inputCls} data-testid="modal-phone-input" />
                <input value={modal.data.email} onChange={(e) => setModal({ ...modal, data: { ...modal.data, email: e.target.value } })} placeholder={S.email[lang]} className={`${inputCls} sm:col-span-2`} data-testid="modal-email-input" />
                <input type="date" value={modal.data.date} onChange={(e) => setModal({ ...modal, data: { ...modal.data, date: e.target.value } })} className={inputCls} data-testid="modal-date-input" />
                <select value={modal.data.time} onChange={(e) => setModal({ ...modal, data: { ...modal.data, time: e.target.value } })} className={inputCls} data-testid="modal-time-select">
                  {TIME_SLOTS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select value={modal.data.advisor} onChange={(e) => setModal({ ...modal, data: { ...modal.data, advisor: e.target.value } })} className={inputCls} data-testid="modal-advisor-select">
                  {Object.entries(ADVISORS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <select value={modal.data.mode} onChange={(e) => setModal({ ...modal, data: { ...modal.data, mode: e.target.value } })} className={inputCls} data-testid="modal-mode-select">
                  {Object.entries(MODES).map(([k, v]) => <option key={k} value={k}>{v[lang]}</option>)}
                </select>
                <input value={modal.data.topic} onChange={(e) => setModal({ ...modal, data: { ...modal.data, topic: e.target.value } })} placeholder={S.topic[lang]} className={`${inputCls} sm:col-span-2`} data-testid="modal-topic-input" />
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button onClick={() => setModal(null)} data-testid="modal-cancel-button" className="rounded-full border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">
                  {S.cancel[lang]}
                </button>
                <button onClick={saveModal} data-testid="modal-save-button" className="rounded-full bg-[#0A192F] px-5 py-2.5 text-sm font-semibold text-white hover:bg-teal-700">
                  {S.save[lang]}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
